import { parsearIphone } from "@/components/iphones/logica";

/**
 * Los colores de iPhone, como los llama Apple y como se ven.
 *
 * POR QUE ESTA TABLA EXISTE
 * -------------------------
 * El color dejó de estar adentro del nombre y pasó a ser una columna. Una
 * columna con la palabra "SIERRA BLUE" repetida no dice mucho más que antes;
 * un cuadradito del color sí, porque es como se mira un mostrador: el cliente
 * no pide "el Sierra Blue", pide "el azulito".
 *
 * Y de paso la tabla enseña el nombre correcto. Quien carga escribe "BLACK" en
 * un 15 Pro y Apple a ese color lo llama Black Titanium; el tooltip del
 * cuadradito lo dice, sin obligar a nadie a escribirlo así.
 *
 * LOS HEX SON APROXIMACIONES
 * --------------------------
 * Apple no publica valores; estos salen de las fotos oficiales de cada
 * lanzamiento y están para RECONOCER un color en un cuadrado de 12 px, no para
 * imprimir nada. Un "más o menos" en el tono no rompe nada; un color inventado
 * para un nombre que no conocemos sí, y por eso lo que no está en la tabla
 * cae en `null` y la columna muestra el texto tal cual se cargó.
 *
 * LO QUE ESTA TABLA NO HACE
 * -------------------------
 * No distingue la misma palabra entre generaciones. El "BLUE" del 13 es más
 * intenso que el del 15, pero Apple ya separa con el NOMBRE los casos que
 * importan —Sierra Blue (13 Pro), Blue Titanium (15 Pro), Deep Blue (17
 * Pro)—, así que la ambigüedad que queda es entre tonos parecidos del mismo
 * color. Si alguna vez molesta, la salida es un segundo mapa por generación,
 * no adivinar acá.
 */

export interface ColorApple {
  /** Como lo llama Apple, con mayúsculas y minúsculas de verdad. */
  etiqueta: string;
  /** Aproximación para el cuadradito. */
  hex: string;
  /** Un color claro necesita borde propio o desaparece sobre el fondo blanco. */
  claro?: boolean;
}

/**
 * Nombre normalizado (mayúsculas, sin acentos, sin «(PRODUCT)») → color.
 *
 * El orden de la tabla es el de los lanzamientos, que es como se recuerdan.
 */
const COLORES: Record<string, ColorApple> = {
  // ── Genéricos, los que se escriben a mano en el mostrador ──────────────────
  BLACK: { etiqueta: "Black", hex: "#1F2124" },
  WHITE: { etiqueta: "White", hex: "#F5F4F0", claro: true },
  RED: { etiqueta: "(PRODUCT)RED", hex: "#BA0C2E" },
  BLUE: { etiqueta: "Blue", hex: "#557A95" },
  GREEN: { etiqueta: "Green", hex: "#3F5449" },
  PURPLE: { etiqueta: "Purple", hex: "#E0DAEA", claro: true },
  PINK: { etiqueta: "Pink", hex: "#E8C4C7", claro: true },
  YELLOW: { etiqueta: "Yellow", hex: "#F2E3A2", claro: true },
  GOLD: { etiqueta: "Gold", hex: "#E3CCA1", claro: true },
  SILVER: { etiqueta: "Silver", hex: "#E3E4E5", claro: true },
  GRAPHITE: { etiqueta: "Graphite", hex: "#54524F" },
  // El 11 Pro: el gris espacial y el verde noche son suyos, no del 12 Pro.
  "SPACE GRAY": { etiqueta: "Space Gray", hex: "#535150" },
  "MIDNIGHT GREEN": { etiqueta: "Midnight Green", hex: "#4E5851" },
  MIDNIGHT: { etiqueta: "Midnight", hex: "#2E3641" },
  STARLIGHT: { etiqueta: "Starlight", hex: "#FAF6F2", claro: true },

  // ── iPhone 12 Pro ─────────────────────────────────────────────────────────
  "PACIFIC BLUE": { etiqueta: "Pacific Blue", hex: "#2D4E5E" },

  // ── iPhone 13 Pro ─────────────────────────────────────────────────────────
  "SIERRA BLUE": { etiqueta: "Sierra Blue", hex: "#9BB5CE", claro: true },
  "ALPINE GREEN": { etiqueta: "Alpine Green", hex: "#505F4E" },

  // ── iPhone 14 Pro ─────────────────────────────────────────────────────────
  "DEEP PURPLE": { etiqueta: "Deep Purple", hex: "#574E5F" },
  "SPACE BLACK": { etiqueta: "Space Black", hex: "#35383B" },

  // ── iPhone 15 Pro y 16 Pro: la era del titanio ────────────────────────────
  "BLACK TITANIUM": { etiqueta: "Black Titanium", hex: "#3C3C3D" },
  "WHITE TITANIUM": { etiqueta: "White Titanium", hex: "#F2F1ED", claro: true },
  "NATURAL TITANIUM": { etiqueta: "Natural Titanium", hex: "#BFB6AC", claro: true },
  "BLUE TITANIUM": { etiqueta: "Blue Titanium", hex: "#56607A" },
  "DESERT TITANIUM": { etiqueta: "Desert Titanium", hex: "#BFA48F", claro: true },

  // ── iPhone 16 ─────────────────────────────────────────────────────────────
  ULTRAMARINE: { etiqueta: "Ultramarine", hex: "#4F5FCB" },
  TEAL: { etiqueta: "Teal", hex: "#B4D5D0", claro: true },

  // ── iPhone 17 y 17 Pro ────────────────────────────────────────────────────
  LAVENDER: { etiqueta: "Lavender", hex: "#D6CCE4", claro: true },
  "MIST BLUE": { etiqueta: "Mist Blue", hex: "#C9D6E0", claro: true },
  SAGE: { etiqueta: "Sage", hex: "#C7CFC0", claro: true },
  "COSMIC ORANGE": { etiqueta: "Cosmic Orange", hex: "#F4643C" },
  "DEEP BLUE": { etiqueta: "Deep Blue", hex: "#2F3E56" },

  // ── iPhone 18 Pro (presentado el 09/09/2026) ──────────────────────────────
  BURGUNDY: { etiqueta: "Burgundy", hex: "#6E2639" },
  GLACIER: { etiqueta: "Glacier", hex: "#C9DCE6", claro: true },

  // ── iPhone Air ────────────────────────────────────────────────────────────
  "CLOUD WHITE": { etiqueta: "Cloud White", hex: "#EDEBE6", claro: true },
  "LIGHT GOLD": { etiqueta: "Light Gold", hex: "#D9C7A7", claro: true },
  "SKY BLUE": { etiqueta: "Sky Blue", hex: "#AFC4D6", claro: true },

  // ── iPhone 17e (marzo de 2026) ────────────────────────────────────────────
  // No es el PINK del 13 ni del 15: es mas lavado, casi un beige rosado.
  "SOFT PINK": { etiqueta: "Soft Pink", hex: "#EFD9D8", claro: true },

  // ── iPhone Duo, el plegable (presentado el 09/09/2026) ────────────────────
  // Los dos son titanio grado 5 pulido espejo, asi que el cuadradito tira mas
  // a metal que a pintura: el blanco es calido y el oscuro es un azul casi
  // negro, no un negro.
  "STAR WHITE": { etiqueta: "Star White", hex: "#E9E6DF", claro: true },
  "NIGHT SKY": { etiqueta: "Night Sky", hex: "#232A38" },
};

/**
 * Lo que se escribe en el mostrador y no es exactamente el nombre de Apple.
 * Son abreviaturas reales del catálogo, no inventos: «DESERT» por Desert
 * Titanium es el caso que aparece hoy en la base.
 */
const ALIAS: Record<string, string> = {
  DESERT: "DESERT TITANIUM",
  NATURAL: "NATURAL TITANIUM",
  TITANIUM: "NATURAL TITANIUM",
  "PRODUCT RED": "RED",
  "SPACE GREY": "SPACE GRAY",
  NEGRO: "BLACK",
  BLANCO: "WHITE",
  ROJO: "RED",
  AZUL: "BLUE",
  VERDE: "GREEN",
  VIOLETA: "PURPLE",
  MORADO: "PURPLE",
  ROSA: "PINK",
  DORADO: "GOLD",
  PLATA: "SILVER",
  AMARILLO: "YELLOW",
};

function normalizar(color: string): string {
  return color
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\(PRODUCT\)/gi, "")
    .replace(/[^a-zA-Z\s]/g, " ")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

/**
 * El color de Apple que corresponde a lo que se escribió, o `null` si no lo
 * conocemos. `null` no es un error: es la respuesta honesta para «AZUL CLARITO
 * TIRANDO A GRIS», y la columna muestra ese texto en vez de un cuadrado que
 * miente.
 */
/**
 * El nombre en castellano, para la tienda.
 *
 * El de Apple se queda: es el de la caja, el de las fotos y el que el local
 * busca en el stock. Pero «Midnight» o «Starlight» no le dicen nada a quien no
 * habla ingles, asi que la tienda muestra los dos. Las traducciones son las de
 * Apple en castellano donde las hay («Medianoche», «Blanco estelar»).
 */
const EN_CASTELLANO: Record<string, string> = {
  Black: "Negro",
  White: "Blanco",
  "(PRODUCT)RED": "Rojo",
  Blue: "Azul",
  Green: "Verde",
  Purple: "Violeta",
  Pink: "Rosa",
  Yellow: "Amarillo",
  Gold: "Dorado",
  Silver: "Plata",
  Graphite: "Grafito",
  "Space Gray": "Gris espacial",
  "Midnight Green": "Verde noche",
  Midnight: "Medianoche",
  Starlight: "Blanco estelar",
  "Pacific Blue": "Azul pacífico",
  "Sierra Blue": "Azul sierra",
  "Alpine Green": "Verde alpino",
  "Deep Purple": "Morado oscuro",
  "Space Black": "Negro espacial",
  "Black Titanium": "Titanio negro",
  "White Titanium": "Titanio blanco",
  "Natural Titanium": "Titanio natural",
  "Blue Titanium": "Titanio azul",
  "Desert Titanium": "Titanio desierto",
  Ultramarine: "Azul ultramar",
  Teal: "Verde azulado",
  Lavender: "Lavanda",
  "Mist Blue": "Azul niebla",
  Sage: "Verde salvia",
  "Cosmic Orange": "Naranja cósmico",
  "Deep Blue": "Azul profundo",
  Burgundy: "Bordó",
  Glacier: "Azul glaciar",
  "Cloud White": "Blanco nube",
  "Light Gold": "Dorado claro",
  "Sky Blue": "Celeste",
  "Soft Pink": "Rosa suave",
  "Star White": "Blanco estrella",
  "Night Sky": "Azul noche",
};

/** «Midnight (medianoche)»: el de Apple y, si lo hay, el castellano. */
export function colorConTraduccion(etiqueta: string): string {
  const es = EN_CASTELLANO[etiqueta];
  return es && es.toLowerCase() !== etiqueta.toLowerCase()
    ? `${etiqueta} (${es.toLowerCase()})`
    : etiqueta;
}

export function colorDeIphone(color: string | null): ColorApple | null {
  if (!color?.trim()) return null;
  const clave = normalizar(color);
  return COLORES[ALIAS[clave] ?? clave] ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// En que colores viene cada modelo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * UN SELLADO NO TIENE UN COLOR: TIENE LOS DEL MODELO.
 *
 * Un usado es una unidad fisica y su color es un dato de esa unidad. Un
 * sellado se pide, y se pide en el color que el cliente quiera de los que Apple
 * hizo para ese modelo. Por eso la ficha no lo trae cargado y no es un olvido:
 * escribir uno seria elegir por el cliente.
 *
 * Lo que si hace falta contestar en el mostrador es "¿en que colores viene el
 * 17 Pro?", y eso no esta en ningun lado del sistema salvo en la cabeza del que
 * atiende. Esta tabla es esa respuesta.
 *
 * La clave es el modelo como lo devuelve `parsearIphone()`: "17 PRO MAX", "16",
 * "15 PLUS". Un modelo que no este aca no inventa nada: la columna queda vacia.
 */
export const COLORES_POR_MODELO: Record<string, ReadonlyArray<string>> = {
  "11": ["BLACK", "WHITE", "GREEN", "YELLOW", "PURPLE", "RED"],
  "11 PRO": ["MIDNIGHT GREEN", "SPACE GRAY", "SILVER", "GOLD"],
  "11 PRO MAX": ["MIDNIGHT GREEN", "SPACE GRAY", "SILVER", "GOLD"],

  "12": ["BLACK", "WHITE", "RED", "GREEN", "BLUE", "PURPLE"],
  "12 MINI": ["BLACK", "WHITE", "RED", "GREEN", "BLUE", "PURPLE"],
  "12 PRO": ["SILVER", "GRAPHITE", "GOLD", "PACIFIC BLUE"],
  "12 PRO MAX": ["SILVER", "GRAPHITE", "GOLD", "PACIFIC BLUE"],

  "13": ["MIDNIGHT", "STARLIGHT", "BLUE", "PINK", "GREEN", "RED"],
  "13 MINI": ["MIDNIGHT", "STARLIGHT", "BLUE", "PINK", "GREEN", "RED"],
  "13 PRO": ["GRAPHITE", "SILVER", "GOLD", "SIERRA BLUE", "ALPINE GREEN"],
  "13 PRO MAX": ["GRAPHITE", "SILVER", "GOLD", "SIERRA BLUE", "ALPINE GREEN"],

  "14": ["MIDNIGHT", "STARLIGHT", "BLUE", "PURPLE", "YELLOW", "RED"],
  "14 PLUS": ["MIDNIGHT", "STARLIGHT", "BLUE", "PURPLE", "YELLOW", "RED"],
  "14 PRO": ["SPACE BLACK", "SILVER", "GOLD", "DEEP PURPLE"],
  "14 PRO MAX": ["SPACE BLACK", "SILVER", "GOLD", "DEEP PURPLE"],

  "15": ["BLACK", "BLUE", "GREEN", "YELLOW", "PINK"],
  "15 PLUS": ["BLACK", "BLUE", "GREEN", "YELLOW", "PINK"],
  "15 PRO": ["BLACK TITANIUM", "WHITE TITANIUM", "NATURAL TITANIUM", "BLUE TITANIUM"],
  "15 PRO MAX": ["BLACK TITANIUM", "WHITE TITANIUM", "NATURAL TITANIUM", "BLUE TITANIUM"],

  "16": ["BLACK", "WHITE", "PINK", "TEAL", "ULTRAMARINE"],
  "16 PLUS": ["BLACK", "WHITE", "PINK", "TEAL", "ULTRAMARINE"],
  "16E": ["BLACK", "WHITE"],
  "16 PRO": ["BLACK TITANIUM", "WHITE TITANIUM", "NATURAL TITANIUM", "DESERT TITANIUM"],
  "16 PRO MAX": ["BLACK TITANIUM", "WHITE TITANIUM", "NATURAL TITANIUM", "DESERT TITANIUM"],

  "17": ["BLACK", "WHITE", "LAVENDER", "MIST BLUE", "SAGE"],
  "17E": ["BLACK", "WHITE", "SOFT PINK"],
  "17 PRO": ["COSMIC ORANGE", "DEEP BLUE", "SILVER"],
  "17 PRO MAX": ["COSMIC ORANGE", "DEEP BLUE", "SILVER"],

  // Presentados el 09/09/2026.
  "18 PRO": ["BURGUNDY", "GLACIER", "BLACK", "SILVER"],
  "18 PRO MAX": ["BURGUNDY", "GLACIER", "BLACK", "SILVER"],

  AIR: ["SPACE BLACK", "CLOUD WHITE", "LIGHT GOLD", "SKY BLUE"],
  // El plegable. No lleva numero de generacion, igual que el Air.
  DUO: ["STAR WHITE", "NIGHT SKY"],
  SE: ["MIDNIGHT", "STARLIGHT", "RED"],
};

/**
 * En que colores viene ese modelo, segun el nombre del producto.
 *
 * Devuelve vacio para un modelo que la tabla no conoce —un Samsung, un iPhone
 * que todavia no cargamos—. Vacio es la respuesta honesta: la alternativa
 * seria mostrar los colores de otro telefono.
 */
export function coloresDelModelo(nombre: string): ColorApple[] {
  const { modelo } = parsearIphone(nombre);
  // `normalizar()` no sirve acá: borra los dígitos, que es lo primero que
  // tiene un modelo. "17 PRO" quedaría en "PRO".
  const clave = modelo.trim().toUpperCase().replace(/\s+/g, " ");
  return (COLORES_POR_MODELO[clave] ?? [])
    .map((c) => COLORES[c])
    .filter((c): c is ColorApple => Boolean(c));
}
